import React, { useMemo } from "react";

function ema(values, period) {
  const k = 2 / (period + 1);
  const out = [];
  let prev = values[0];
  for (let i = 0; i < values.length; i++) {
    if (i === 0) out.push(prev);
    else {
      prev = values[i] * k + prev * (1 - k);
      out.push(prev);
    }
  }
  return out;
}

function rsi(values, period = 14) {
  const out = new Array(values.length).fill(null);
  if (values.length < period + 1) return out;
  let gains = 0, losses = 0;
  for (let i = 1; i <= period; i++) {
    const d = values[i] - values[i - 1];
    if (d >= 0) gains += d; else losses -= d;
  }
  let avgG = gains / period, avgL = losses / period;
  out[period] = avgL === 0 ? 100 : 100 - 100 / (1 + avgG / avgL);
  for (let i = period + 1; i < values.length; i++) {
    const d = values[i] - values[i - 1];
    const g = d > 0 ? d : 0;
    const l = d < 0 ? -d : 0;
    avgG = (avgG * (period - 1) + g) / period;
    avgL = (avgL * (period - 1) + l) / period;
    out[i] = avgL === 0 ? 100 : 100 - 100 / (1 + avgG / avgL);
  }
  return out;
}

function macd(values, fast = 12, slow = 26, signal = 9) {
  const emaFast = ema(values, fast);
  const emaSlow = ema(values, slow);
  const macdLine = values.map((_, i) => emaFast[i] - emaSlow[i]);
  const signalLine = ema(macdLine, signal);
  const histogram = macdLine.map((v, i) => v - signalLine[i]);
  return { macdLine, signalLine, histogram };
}

/**
 * Compact svg-based RSI & MACD panels. Lightweight-charts supports multi-panel
 * but this keeps bundle size down and the look consistent with the app.
 */
export default function IndicatorsPanel({ points = [] }) {
  const closes = useMemo(() => points.map((p) => p.c).filter((v) => v != null), [points]);

  const rsiValues = useMemo(() => rsi(closes, 14), [closes]);
  const macdData = useMemo(() => macd(closes), [closes]);
  const last = (arr) => arr[arr.length - 1];
  const lastRsi = last(rsiValues);
  const lastMacd = last(macdData.macdLine);
  const lastSig = last(macdData.signalLine);
  const lastHist = last(macdData.histogram);

  if (closes.length < 20) {
    return (
      <div className="rounded-2xl bg-white border border-[#EAE6E1] p-6 text-sm text-[#6B7068]" data-testid="indicators-empty">
        Not enough data to calculate indicators in this range.
      </div>
    );
  }

  // Build SVG polylines
  const w = 600, hR = 80;
  const points2Path = (arr, min, max) => {
    const valid = arr.map((v, i) => ({ v, i })).filter((p) => p.v != null);
    return valid.map((p, idx) => {
      const x = (p.i / (arr.length - 1)) * w;
      const y = hR - ((p.v - min) / (max - min || 1)) * hR;
      return `${idx === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(" ");
  };

  // RSI: fixed 0-100 scale
  const rsiPath = points2Path(rsiValues, 0, 100);

  // MACD: dynamic scale
  const macdAll = [...macdData.macdLine, ...macdData.signalLine];
  const macdMin = Math.min(...macdAll);
  const macdMax = Math.max(...macdAll);
  const macdPath = points2Path(macdData.macdLine, macdMin, macdMax);
  const sigPath = points2Path(macdData.signalLine, macdMin, macdMax);

  return (
    <div className="space-y-4" data-testid="indicators-panel">
      {/* RSI */}
      <div className="rounded-2xl bg-white border border-[#EAE6E1] p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#6B7068]">
              Relative Strength Index · 14
            </div>
            <div className="font-mono-ticker text-xl text-[#2A2F2B] mt-0.5">
              {lastRsi != null ? lastRsi.toFixed(1) : "—"}
              <span className="text-xs text-[#6B7068] ml-2 font-normal font-sans">
                {lastRsi > 70 ? "Overbought" : lastRsi < 30 ? "Oversold" : "Neutral"}
              </span>
            </div>
          </div>
        </div>
        <svg viewBox={`0 0 ${w} ${hR}`} className="w-full h-20">
          <line x1="0" y1={hR * 0.3} x2={w} y2={hR * 0.3} stroke="#C66B59" strokeDasharray="3,3" strokeOpacity="0.5" />
          <line x1="0" y1={hR * 0.7} x2={w} y2={hR * 0.7} stroke="#5C8D73" strokeDasharray="3,3" strokeOpacity="0.5" />
          <path d={rsiPath} fill="none" stroke="#547A8F" strokeWidth="1.8" />
        </svg>
        <div className="flex justify-between text-[10px] font-mono-ticker text-[#6B7068] mt-1">
          <span>0</span><span>30</span><span>50</span><span>70</span><span>100</span>
        </div>
      </div>

      {/* MACD */}
      <div className="rounded-2xl bg-white border border-[#EAE6E1] p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#6B7068]">
              MACD · 12 / 26 / 9
            </div>
            <div className="flex items-baseline gap-3 mt-0.5">
              <div className="font-mono-ticker text-xl text-[#2A2F2B]">
                {lastMacd != null ? lastMacd.toFixed(3) : "—"}
              </div>
              <div className="text-xs font-mono-ticker" style={{ color: lastHist >= 0 ? "#5C8D73" : "#C66B59" }}>
                hist {lastHist != null ? (lastHist >= 0 ? "+" : "") + lastHist.toFixed(3) : "—"}
              </div>
            </div>
          </div>
        </div>
        <svg viewBox={`0 0 ${w} ${hR}`} className="w-full h-20">
          <line x1="0" y1={hR - ((0 - macdMin) / (macdMax - macdMin || 1)) * hR} x2={w} y2={hR - ((0 - macdMin) / (macdMax - macdMin || 1)) * hR} stroke="#EAE6E1" />
          {macdData.histogram.map((v, i) => {
            if (v == null) return null;
            const x = (i / (macdData.histogram.length - 1)) * w;
            const zero = hR - ((0 - macdMin) / (macdMax - macdMin || 1)) * hR;
            const y = hR - ((v - macdMin) / (macdMax - macdMin || 1)) * hR;
            const barW = Math.max(1, w / macdData.histogram.length - 0.5);
            return (
              <rect
                key={i}
                x={x - barW / 2}
                y={Math.min(y, zero)}
                width={barW}
                height={Math.abs(y - zero)}
                fill={v >= 0 ? "#5C8D73" : "#C66B59"}
                opacity="0.5"
              />
            );
          })}
          <path d={macdPath} fill="none" stroke="#547A8F" strokeWidth="1.5" />
          <path d={sigPath} fill="none" stroke="#D18E63" strokeWidth="1.5" />
        </svg>
        <div className="flex items-center gap-4 text-[10px] mt-1 font-mono-ticker text-[#6B7068]">
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#547A8F]" />MACD</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#D18E63]" />Signal</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-[#5C8D73]/50" />Histogram</span>
        </div>
      </div>
    </div>
  );
}
